import Callout from "components/callout/callout"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { AccessRole, SortDirection } from 'types'
import useLocations from "hooks/useLocations"
import React, { JSX } from "react"
import IconImage from "components/image/IconImage"
import Section from "components/layout/section"
import { ReactComponent as LocationIcon } from "assets/icons/location.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Button from "components/inputs/button"
import DocList, { ViewMode } from "components/list/docList/docList"
import TableList from "components/list/tableList/tableList"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import LocationFilter from "./LocationFilter"
import { logger } from "utils/secureLogger"
import useAccess from "@/hooks/useAccess"

interface LocationListProps {
    storageId: string
}

const LocationList: React.FC<LocationListProps> = ({ storageId }) => {

    const { t } = useTranslation()

    const {
        locations,
        allLocations,
        filteredLocations,
        locationsStatus,
        locationsInitialized,
        filterQuery,
        setFilterQuery,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        displayDeleted,
        setDisplayDeleted
    } = useLocations({ storageId })
    
    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    const allowedSortBy = ["name"] as const

    logger.debug("Locations loaded", { locationsInitialized, locationCount: allLocations.length })

    if (!storageId) return <Callout type={`error`}>No storage id.</Callout>
    if (locationsStatus === `error`) {
        return <Callout type={`error`}>{t('failedToLoadStorage')}</Callout>
    }
    if (locationsInitialized && allLocations.length === 0 && !filterQuery && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR])) {
        return (
            <Section design={`card-centered`}>
                <IconImage>
                    <LocationIcon />
                </IconImage>
                <h2>{t(`titleNoLocations`)}</h2>
                <p>{t('locationListNoLocations')}</p>
                <Link className="block w-full md:inline-block md:w-fit" to={`/storage/locations/create`}><Button wideOnMobile design="primary"><AddIcon /><span>{t('buttonCreateLocation')}</span></Button></Link>
            </Section>
        )
    }

    const printHierarchy = (itemId: string, level: number, key: number): JSX.Element | null => {
        const locationData = allLocations.find(c => c.id === itemId)
        if (!locationData) return null
        if (locationData.isDeleted && !displayDeleted) {
            return (
                <React.Fragment key={key}>
            {allLocations.map((childLocation, key_ii) => {
                        if (childLocation.relations?.parentLocation === itemId) {
                if (!childLocation.id) return null
                return printHierarchy(childLocation.id, level, key_ii)
                        }
                        return null
                    })}
                </React.Fragment>
            )
        }
        return (
            <React.Fragment key={key}>
                <TableListRow to={`/storage/locations/${itemId}`} isDeleted={locationData.isDeleted}>
                    <TableListCell>
                        {Array(level).fill('\u2014').join('')}{level ? ` ` : null}{locationData.name}
                    </TableListCell>
                    <TableListCell>
                        {locationData.description || '-'}
                    </TableListCell>
                </TableListRow>
        {allLocations.map((childLocation, key_ii) => {
                    if (childLocation.relations?.parentLocation === itemId) {
            if (!childLocation.id) return null
            return printHierarchy(childLocation.id, level + 1, key_ii)
                    }
                    return null
                })}
            </React.Fragment>
        )
    }

    return (
        <DocList
            id={`location_list`}
            viewModes={[ViewMode.Table]}
            defaultViewMode={ViewMode.Table}
            filterElement={
                <LocationFilter
                    filterQuery={filterQuery}
                    setFilterQuery={setFilterQuery}
                    allowedSortBy={allowedSortBy}
                    sortBy={sortBy}
                    setSortBy={(v) => setSortBy(v as any)}
                    sortDirection={sortDirection}
                    setSortDirection={(v) => setSortDirection(v === 'asc' ? SortDirection.ASCENDING : SortDirection.DESCENDING)}
                    isLoading={!locationsInitialized}
                    displayDeleted={displayDeleted}
                    setDisplayDeleted={setDisplayDeleted}
                />
            }
            showResults={(locationsInitialized && filteredLocations.length > 0) || locationsStatus !== `success`}
        >
            <div data-view-mode={ViewMode.Table}>
                <TableList>
                    <TableListRow isHeader={true}>
                        <TableListCell>{t('name')}</TableListCell>
                        <TableListCell>{t('description')}</TableListCell>
                    </TableListRow>
                    {filteredLocations.map((locationData, key) => {
                        if (!locationData.id) return null
                        if (
                            locationData.relations?.parentLocation &&
                            allLocations.some(c => c.id === locationData.relations?.parentLocation)
                        ) return null
                        return printHierarchy(locationData.id, 0, key)
                    })}
                    {locationsStatus === `loading` && !locations.length &&
                        Array.from({ length: 3 }).map((_, key) => (
                            <TableListRow key={key}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    }
                </TableList>
            </div>
        </DocList>
    )
}

export default LocationList
